import React, { useState, useEffect } from 'react';
import { Save, Settings, Globe, CreditCard, Phone, Share2, BarChart3, Layout, Loader2, CheckCircle } from 'lucide-react';
import api from '../../utils/api';

const GROUPS = [
  { key:'general', label:'General',        icon:Globe },
  { key:'hero',    label:'Hero Section',   icon:Layout },
  { key:'about',   label:'Mission & Vision', icon:Settings },
  { key:'stats',   label:'Impact Stats',   icon:BarChart3 },
  { key:'contact', label:'Contact Info',   icon:Phone },
  { key:'social',  label:'Social Media',   icon:Share2 },
  { key:'payment', label:'Payment / Bank', icon:CreditCard },
  { key:'footer',  label:'Footer',         icon:Layout },
];

const TEXTAREA_KEYS = ['site_description','hero_subtitle','mission_statement','vision_statement','footer_about','about_hero_subtitle','team_description','about_cta_subtitle'];

export default function AdminSettings() {
  const [settings, setSettings] = useState([]);
  const [values,   setValues]   = useState({});
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [activeGroup, setActiveGroup] = useState('general');

  useEffect(() => {
    api.get('/settings').then(r => {
      setSettings(r.data);
      const vals = {};
      r.data.forEach(s => { vals[s.key] = s.value; });
      setValues(vals);
    }).finally(() => setLoading(false));
  }, []);

  const grouped = settings.filter(s => s.group === activeGroup);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.post('/settings/bulk', values);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert('Save failed: ' + e.message); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64 gap-2 text-gray-400">
      <Loader2 size={20} className="animate-spin" /> Loading settings...
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Settings size={22} className="text-royal-800" /> Site Settings
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">All public-facing static content is editable here — no code changes needed.</p>
        </div>
        <button onClick={save} disabled={saving}
          className="btn-royal text-sm py-2.5 px-5 flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 size={15} className="animate-spin" /> : saved ? <CheckCircle size={15} /> : <Save size={15} />}
          {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Changes'}
        </button>
      </div>

      <div className="flex gap-5 flex-col lg:flex-row">
        {/* Sidebar */}
        <div className="lg:w-52 flex-shrink-0">
          <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-2 flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible scrollbar-hide">
            {GROUPS.map(g => (
              <button key={g.key} onClick={() => setActiveGroup(g.key)}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                  activeGroup === g.key ? 'bg-royal-800 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}>
                <g.icon size={16} className="flex-shrink-0" />{g.label}
              </button>
            ))}
          </div>
        </div>

        {/* Fields */}
        <div className="flex-1 bg-white rounded-2xl shadow-card border border-gray-100 p-6">
          <h2 className="font-display font-semibold text-gray-900 mb-1 flex items-center gap-2">
            {(() => { const g = GROUPS.find(x => x.key === activeGroup); return g ? <><g.icon size={18} className="text-royal-800" />{g.label}</> : null; })()}
          </h2>
          <p className="text-xs text-gray-400 mb-6">Changes here update the live website instantly after saving.</p>

          {activeGroup === 'payment' && (
            <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
              <strong>Paystack:</strong> Get your public key from your{' '}
              <a href="https://dashboard.paystack.com/#/settings/developer" target="_blank" rel="noreferrer" className="underline font-semibold">Paystack Dashboard → Settings → API Keys</a>.
              Only enter the <code className="bg-amber-100 px-1 rounded">pk_live_</code> or <code className="bg-amber-100 px-1 rounded">pk_test_</code> key — never the secret key.
            </div>
          )}

          {activeGroup === 'stats' && (
            <div className="mb-5 p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
              These numbers display on the homepage impact counter section. Enter numeric values only (no commas or symbols).
            </div>
          )}

          <div className="space-y-5">
            {grouped.length === 0 && <p className="text-gray-400 text-sm">No settings in this group.</p>}
            {grouped.map(s => (
              <div key={s.key}>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{s.label}</label>
                {TEXTAREA_KEYS.includes(s.key) ? (
                  <textarea rows={4} value={values[s.key] ?? ''} onChange={e => setValues(p => ({ ...p, [s.key]: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all resize-y min-h-[80px]" />
                ) : (
                  <input
                    type={s.key.includes('password') || s.key.includes('secret') ? 'password' : 'text'}
                    value={values[s.key] ?? ''} onChange={e => setValues(p => ({ ...p, [s.key]: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-royal-800/20 focus:border-royal-800 transition-all bg-white" />
                )}
                <p className="text-[10px] text-gray-300 mt-1 font-mono">{s.key}</p>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-6 pt-5 border-t border-gray-100">
            <button onClick={save} disabled={saving}
              className="btn-royal text-sm py-2.5 px-6 flex items-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}